import Immutable from 'immutable';

import type {
    Blueprint,
    BlueprintAutoPickResponse,
    BlueprintsListData,
    NarrativeThreadEnum,
    PublicBlueprintsListData,
    PublicBlueprintUseCase,
} from 'js/src/client';
import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { setBlueprintId, setRecommendedBlueprint } from 'js/src/data/app';
import {
    getCustomBlueprint,
    pushBlueprint,
    pushBlueprints,
    setBlueprintName,
    setBlueprintsLoading,
} from 'js/src/data/blueprints';
import { loadLuminaryPreviewsOperation } from 'js/src/data/thumbnail-previews';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';
import WebSocketClient from 'js/src/libs/websocket-client';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';

/**
 * Fetches and sets the available public blueprints.
 * @param narrative_thread the narrative thread of the video we're creating
 * @param template the template of the video we're creating
 * @param max_results the maximum number of blueprints to fetch
 * @param ids the ids of the blueprints to fetch
 * @param use_case the use case of the blueprints to fetch
 */
export const fetchPublicBlueprints = ({
    narrative_thread,
    template,
    max_results,
    ids,
    use_case,
}: {
    narrative_thread?: string;
    template?: string;
    max_results?: number;
    ids?: number[];
    use_case?: PublicBlueprintUseCase;
}) => {
    return async (dispatch: AppDispatch) => {
        // dedupe ids if set
        if (ids && ids.length > 0) {
            ids = Array.from(new Set(ids));
        }
        try {
            const query: PublicBlueprintsListData['query'] = {
                ...(narrative_thread && { narrative_thread }),
                ...(template && { template }),
                ...(max_results && { max_results }),
                ...(ids && { ids: ids ? ids.join(',') : undefined }),
                ...(use_case && { use_case }),
            };
            const response = await ApiService.publicBlueprintsList({ query });
            dispatch(pushBlueprints({ blueprints: response.data }));
            return { success: true, blueprints: response.data };
        } catch (error) {
            ErrorReporter.capture(error);
            return { success: false, blueprints: [] };
        }
    };
};

/**
 * Fetches and sets the available public & custom blueprints.
 * @param narrative_thread the narrative thread of the video we're creating
 * @param template the template of the video we're creating
 * @param max_results the maximum number of blueprints to fetch
 * @param ids the ids of the blueprints to fetch
 * @param use_case the use case of the blueprints to fetch
 */
export const fetchBlueprints = ({
    narrative_thread,
    template,
    max_results,
    ids,
    use_case,
}: {
    narrative_thread?: string;
    template?: string;
    max_results?: number;
    ids?: number[];
    use_case?: PublicBlueprintUseCase;
}) => {
    return async (dispatch: AppDispatch) => {
        // dedupe ids if set
        if (ids && ids.length > 0) {
            ids = Array.from(new Set(ids));
        }
        try {
            const query: BlueprintsListData['query'] = {
                ...(narrative_thread && { narrative_thread }),
                ...(template && { template }),
                ...(max_results && { max_results }),
                ...(ids && { ids: ids ? ids.join(',') : undefined }),
                ...(use_case && { use_case }),
            };
            const response = await ApiService.blueprintsList({ query });
            dispatch(pushBlueprints({ blueprints: response.data }));
            return { success: true, blueprints: response.data };
        } catch (error) {
            ErrorReporter.capture(error);
            return { success: false, blueprints: [] };
        }
    };
};

export const getOrFetchBlueprint = (blueprintId: number) => {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<Blueprint | null> => {
        const blueprint = getCustomBlueprint(getState(), blueprintId);
        if (blueprint) {
            return blueprint;
        }
        try {
            const response = await ApiService.blueprintsRetrieve({
                path: { id: blueprintId },
            });
            const blueprint = response.data;
            dispatch(pushBlueprint({ blueprint }));
            return blueprint;
        } catch (e: unknown) {
            ErrorReporter.capture(e);
            return null;
        }
    };
};

/**
 * Returns the provided blueprint id if it's valid and matches the specified narrative thread, otherwise null.
 * This is useful when making video recommendation requests where we only want to use the blueprint id if it matches
 * the narrative thread of the request.
 */
export const blueprintIdIfItMatchesNarrativeThread = (
    blueprintId: number | null,
    narrativeThread: NarrativeThreadEnum,
) => {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<number | null> => {
        if (blueprintId === null) {
            return null;
        }
        const blueprint = await dispatch(getOrFetchBlueprint(blueprintId));
        if (blueprint === null) {
            // can't find details, assume not valid
            return null;
        }
        if (blueprint.narrative_thread !== narrativeThread) {
            // mismatch!
            return null;
        }
        return blueprintId;
    };
};

export function updateBlueprintName({ blueprintId, name }: { blueprintId: number; name: string }) {
    return (dispatch: AppDispatch) => {
        ApiService.customBlueprintsPartialUpdate({
            path: { id: blueprintId },
            body: {
                name,
            },
        })
            .then((response) => {
                dispatch(
                    setBlueprintName({
                        name: response.data.name,
                        blueprintId,
                    }),
                );
            })
            .catch((e: unknown) => {
                ErrorReporter.capture(e);
                Toast.error(
                    'Something went wrong with renaming your blueprint, please try again later.',
                );
            });
    };
}

// Create a new customer blueprint from a source video. The child video of the blueprint is an archived duplicate of the source video.
export const createCustomerBlueprintOperation = createLuminaryOperation(
    async (
        { dispatch, getState, call },
        {
            videoId,
            name,
            loadPreview,
            customInstructions,
        }: { videoId: number; name: string; loadPreview?: boolean; customInstructions?: string },
    ) => {
        try {
            const response = await ApiService.videosDuplicateVideosCreate({
                body: {
                    video_ids: [videoId],
                    with_prefix: true,
                    archived: true,
                },
            });
            const video = response.data[0];
            const { data: blueprint } = await ApiService.customBlueprintsCreateFromSimilarCreate({
                body: {
                    video_id: video.id,
                    archived: false,
                    name,
                    custom_instructions: customInstructions,
                },
            });
            dispatch(pushBlueprint({ blueprint }));
            if (loadPreview && !blueprint.poster_url) {
                // Fetch video_data separately for Luminary preview generation
                const videoDataResponse = await ApiService.customBlueprintsVideoDataRetrieve({
                    query: { ids: String(blueprint.id) },
                });
                const videoDataMap = videoDataResponse.data as Record<string, string>;
                const videoData = videoDataMap[blueprint.id];
                if (videoData) {
                    call(loadLuminaryPreviewsOperation, {
                        newVideoDataList: [JSON.parse(videoData)],
                        newIdList: Immutable.List([blueprint.id]),
                        videoIdsForPosterUpload: Immutable.List([blueprint.video_ids?.[0]]),
                    });
                }
            }
            return blueprint;
        } catch (error) {
            ErrorReporter.capture(error);
            Toast.error('Error creating Blueprint from video. Please try again.');
            return null;
        }
    },
);

/**
 * Fetches customer blueprints
 * Only returns blueprints that are not archived and not temporary
 * Also loads luminary previews for blueprint thumbnails using the video_data fetched separately
 */
export const loadCustomerBlueprintsOperation = createLuminaryOperation(
    async ({ dispatch, call }, renderPreviews = true) => {
        dispatch(setBlueprintsLoading({ loading: true }));

        try {
            const response = await ApiService.customBlueprintsList({
                query: {
                    archived: false,
                    temporary: false,
                },
            });

            dispatch(pushBlueprints({ blueprints: response.data }));

            if (renderPreviews && response.data.length > 0) {
                // Find blueprints that need thumbnail generation (no poster_url)
                const blueprintsNeedingThumbnails = response.data.filter(
                    (blueprint) => !blueprint.poster_url,
                );

                if (blueprintsNeedingThumbnails.length > 0) {
                    // Fetch video_data only for blueprints that need thumbnails
                    const ids = blueprintsNeedingThumbnails.map((b) => b.id).join(',');
                    const videoDataResponse = await ApiService.customBlueprintsVideoDataRetrieve({
                        query: { ids },
                    });

                    // Filter to blueprints that have video_data and parse it
                    const videoDataMap = videoDataResponse.data as Record<string, string>;
                    const blueprintsWithVideoData = blueprintsNeedingThumbnails.filter(
                        (blueprint) => videoDataMap[blueprint.id],
                    );
                    const videoDataList = blueprintsWithVideoData.map((blueprint) =>
                        JSON.parse(videoDataMap[blueprint.id]),
                    );

                    const blueprintIds = Immutable.List(
                        blueprintsWithVideoData.map((blueprint) => blueprint.id),
                    );

                    // Get the first video ID from each blueprint to save the generated poster
                    const videoIdsForPosterUpload = Immutable.List(
                        blueprintsWithVideoData.map((blueprint) => blueprint.video_ids?.[0]),
                    );

                    // Load luminary previews if we have video data
                    if (videoDataList.length > 0) {
                        call(loadLuminaryPreviewsOperation, {
                            newVideoDataList: videoDataList,
                            newIdList: blueprintIds,
                            videoIdsForPosterUpload,
                        });
                    }
                }
            }

            dispatch(setBlueprintsLoading({ loading: false }));
            return { success: true, blueprints: response.data };
        } catch (error) {
            dispatch(setBlueprintsLoading({ loading: false }));
            ErrorReporter.capture(error);
            return { success: false, blueprints: [] };
        }
    },
);

/**
 * Automatically picks and sets a blueprint based on reference material and video parameters
 */
export const autoPickBlueprintOperation = ({
    templateId,
    narrativeThread,
    referenceMaterial,
}: {
    templateId: string;
    narrativeThread: NarrativeThreadEnum;
    referenceMaterial: string;
}) => {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        try {
            const createBlueprintFromVideo = async (videoId: number) => {
                try {
                    const { data: blueprint } =
                        await ApiService.customBlueprintsCreateFromSimilarCreate({
                            body: { video_id: videoId },
                        });

                    dispatch(setBlueprintId({ blueprintId: blueprint.id }));

                    analytics.track('Video Blueprint Selected', {
                        blueprintId: blueprint.id,
                        sourceVideoId: videoId,
                        narrativeThread,
                        templateId,
                    });
                } catch (error) {
                    ErrorReporter.capture(error);
                }
            };

            // Fetch recommended blueprint
            const taskResponse = await ApiService.blueprintAutoPickCreate({
                body: {
                    input_content: referenceMaterial,
                    template: templateId,
                    narrative_thread: narrativeThread,
                },
            });

            const ws = WebSocketClient.getInstance();
            const result = await ws.waitForMessage<BlueprintAutoPickResponse>(
                taskResponse.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'blueprint_auto_pick',
            );

            let pickType = 'none';
            if (result.public_blueprint_id !== null) {
                dispatch(setBlueprintId({ blueprintId: result.public_blueprint_id }));
                pickType = 'public';
            } else if (result.custom_blueprint_id !== null) {
                dispatch(setBlueprintId({ blueprintId: result.custom_blueprint_id }));
                pickType = 'custom';
            } else if (result.recent_video_id !== null) {
                await createBlueprintFromVideo(result.recent_video_id);
                pickType = 'recent_video';
            }

            dispatch(
                setRecommendedBlueprint({
                    recommendedBlueprint: {
                        blueprintId: result.public_blueprint_id,
                        customBlueprintId: result.custom_blueprint_id,
                        recentVideoId: result.recent_video_id,
                    },
                }),
            );

            analytics.track('Blueprint Auto Pick', {
                pickedBlueprint: pickType !== 'none',
                publicBlueprintId: result.public_blueprint_id,
                customBlueprintId: result.custom_blueprint_id,
                recentVideoId: result.recent_video_id,
                pickType,
                narrativeThread,
                templateId,
            });

            return {
                success: true,
                pickType,
                blueprintId: result.public_blueprint_id,
                customBlueprintId: result.custom_blueprint_id,
                recentVideoId: result.recent_video_id,
            };
        } catch (error) {
            ErrorReporter.capture(error);
            return {
                success: false,
                pickType: 'none',
                blueprintId: null,
                customBlueprintId: null,
                recentVideoId: null,
            };
        }
    };
};
